/**
 * SynthVoice — procedural engine voice running in an AudioWorklet
 * (public/worklets/enginesound.js, a port of DasEtwas/enginesound, MIT).
 *
 * Replaces the sample RPMMixer for cars that declare `samples.synth`. The
 * physically-informed model (cylinders + intake/exhaust waveguides + muffler)
 * follows rpm continuously — no loops, no crossfades, no licensing of recordings.
 */
import { ENGINE_PRESETS } from './presets';

export interface WaveGuideSpec {
  chamber0: { samples: { delay: number } };
  chamber1: { samples: { delay: number } };
  alpha: number;
  beta: number;
}
export interface CylinderSpec {
  crank_offset: number;
  exhaust_waveguide: WaveGuideSpec;
  intake_waveguide: WaveGuideSpec;
  extractor_waveguide: WaveGuideSpec;
  intake_open_refl: number;
  intake_closed_refl: number;
  exhaust_open_refl: number;
  exhaust_closed_refl: number;
  piston_motion_factor: number;
  ignition_factor: number;
  ignition_time: number;
}
export interface EnginePreset {
  intake_volume: number;
  exhaust_volume: number;
  engine_vibrations_volume: number;
  cylinders: CylinderSpec[];
  intake_noise_factor: number;
  intake_noise_lp: { delay: number };
  engine_vibration_filter: { delay: number };
  muffler: { straight_pipe: WaveGuideSpec; muffler_elements: WaveGuideSpec[] };
  intake_valve_shift: number;
  exhaust_valve_shift: number;
  crankshaft_fluctuation: number;
  crankshaft_fluctuation_lp: { delay: number };
}

/** How a car declares its synthetic engine (CarProfile.samples.synth). */
export interface SynthSpec {
  /** key of ENGINE_PRESETS (or a full preset object) */
  preset: string | EnginePreset;
  /** rebuild the preset with this many evenly-fired cylinders (pipe lengths cycle through the template's) */
  cylinders?: number;
  /** 0..1 irregularity of the crank offsets (cross-plane / odd-fire feel) */
  crankJitter?: number;
  /** multiply every pipe length (longer = deeper, boomier) */
  pipeScale?: number;
  /** output gain fed to the worklet (preset levels are ~-28 dBFS at gain 0.1) */
  gain?: number;
  /** combustion left at zero throttle (0..1) — overrun character */
  overrunIgnition?: number;
  /** intake hiss left at zero throttle (0..1) */
  overrunIntakeNoise?: number;
  /** exhaust level at zero throttle (0..1) */
  offExhaustGain?: number;
}

const WORKLET_URL = '/worklets/enginesound.js?v=1';

function cloneWg(w: WaveGuideSpec, pipeScale: number): WaveGuideSpec {
  return {
    chamber0: { samples: { delay: w.chamber0.samples.delay * pipeScale } },
    chamber1: { samples: { delay: w.chamber1.samples.delay * pipeScale } },
    alpha: w.alpha,
    beta: w.beta,
  };
}

/** Build the engine preset a car asks for (cylinder count / jitter / pipe scale applied). */
export function buildEngine(spec: SynthSpec): EnginePreset {
  const base = typeof spec.preset === 'string' ? ENGINE_PRESETS[spec.preset] ?? ENGINE_PRESETS.example1 : spec.preset;
  const pipeScale = spec.pipeScale ?? 1;
  const n = spec.cylinders ?? base.cylinders.length;
  const jitter = spec.crankJitter ?? 0;
  // deterministic jitter so the same car always sounds the same
  let seed = 1234567;
  const rnd = () => ((seed = (seed * 1664525 + 1013904223) >>> 0) / 4294967296) - 0.5;
  const cylinders: CylinderSpec[] = [];
  for (let i = 0; i < n; i++) {
    const t = base.cylinders[i % base.cylinders.length];
    cylinders.push({
      ...t,
      crank_offset: n === base.cylinders.length && jitter === 0 ? t.crank_offset : ((i / n) + (jitter * rnd()) / n + 1) % 1,
      exhaust_waveguide: cloneWg(t.exhaust_waveguide, pipeScale),
      intake_waveguide: cloneWg(t.intake_waveguide, pipeScale),
      extractor_waveguide: cloneWg(t.extractor_waveguide, pipeScale),
    });
  }
  return {
    ...base,
    cylinders,
    muffler: {
      straight_pipe: cloneWg(base.muffler.straight_pipe, pipeScale),
      muffler_elements: base.muffler.muffler_elements.map((m) => cloneWg(m, pipeScale)),
    },
  };
}

const loadedModules = new WeakSet<BaseAudioContext>();

export class SynthVoice {
  readonly output: GainNode;
  private node: AudioWorkletNode | null = null;
  private rpmParam: AudioParam | null = null;
  private thrParam: AudioParam | null = null;
  dampened = false;

  constructor(private ctx: BaseAudioContext, private spec: SynthSpec) {
    this.output = ctx.createGain();
    this.output.gain.value = 1;
  }

  static supported(): boolean {
    return typeof AudioWorkletNode !== 'undefined';
  }

  async start(): Promise<void> {
    const ctx = this.ctx;
    if (!loadedModules.has(ctx)) {
      await ctx.audioWorklet.addModule(WORKLET_URL);
      loadedModules.add(ctx);
    }
    const engine = buildEngine(this.spec);
    const node = new AudioWorkletNode(ctx, 'roardrive-enginesound', {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [1],
      processorOptions: {
        engine,
        overrunIgnition: this.spec.overrunIgnition,
        overrunIntakeNoise: this.spec.overrunIntakeNoise,
        offExhaustGain: this.spec.offExhaustGain,
      },
    });
    node.port.onmessage = (e: MessageEvent) => {
      if (e.data?.type === 'dampened') this.dampened = !!e.data.value;
    };
    const gain = node.parameters.get('gain');
    if (gain) gain.value = this.spec.gain ?? 0.1;
    this.rpmParam = node.parameters.get('rpm') ?? null;
    this.thrParam = node.parameters.get('throttle') ?? null;
    node.connect(this.output);
    this.node = node;
  }

  /** Called every drivetrain tick: rpm + 0..1 throttle. */
  update(rpm: number, throttle: number): void {
    if (!this.rpmParam || !this.thrParam) return;
    const t = this.ctx.currentTime;
    // short ramps: the worklet smooths the pedal itself, rpm just needs to be continuous
    this.rpmParam.setTargetAtTime(Math.max(0, rpm), t, 0.012);
    this.thrParam.setTargetAtTime(Math.max(0, Math.min(1, throttle)), t, 0.02);
  }

  stop(): void {
    try {
      this.node?.disconnect();
    } catch {
      /* ignore */
    }
    this.node = null;
    this.rpmParam = null;
    this.thrParam = null;
  }
}
